import {useQuery} from '@apollo/client';
import {Select} from 'antd';
import {PaginatedSelect, usePaginatedOptions} from 'components/paginatedSelect';
import {GET_ALL_COLLECTIONS} from 'graphql/queries/collections';
import {GET_ASSET_CLASS_INSTRUMENTS} from 'graphql/queries/assetManagement';
import {GET_SAVING_COMPANIES} from 'graphql/queries/savings';
import {GET_ALL_BROKERS, GET_ALL_STOCKS} from 'graphql/queries/stocks';
import {brokerOptionLabel} from '../brokerVenues';

/**
 * The option sources behind the order filter.
 *
 * Three of them page and search on the server — instruments, collections and
 * (through its own component) users. Brokers, exchanges and organisations do
 * not: `getBrokers` and `getAllStocksExchange` take no arguments at all, and
 * `getOrganizations` takes an `OrganizationFilterInput` whose fields are not
 * documented anywhere in this app — the savings screen passes `filters` to a
 * query that declares `$filter`, so its page and limit have never reached the
 * server. Those three fetch their list and search it in the browser, which is
 * honest for a list of ten brokers and four exchanges, and is called out here
 * rather than dressed up as pagination.
 */

/** Instruments for the chosen instrument type + asset class. */
export const InstrumentSelect = ({
  value,
  onChange,
  instrumentKey,
  assetClass,
  skip,
}) => {
  const pagination = usePaginatedOptions({
    query: GET_ASSET_CLASS_INSTRUMENTS,
    skip: skip || !instrumentKey || !assetClass,
    buildVariables: (page, search, limit) => ({
      input: {
        explore: false,
        instrumentKey,
        assetClassKey: assetClass,
        paginate: true,
        page,
        limit,
        ...(search && {search}),
      },
    }),
    readPage: data => ({
      rows: (data?.getFinancialInstruments?.data ?? []).filter(row => row?.id),
      hasNextPage: data?.getFinancialInstruments?.pagination?.hasNextPage,
    }),
    toOption: row => ({
      value: row.id,
      label: row.name,
      sub: row.ticker,
    }),
  });

  return (
    <PaginatedSelect
      value={value}
      onChange={onChange}
      pagination={pagination}
      placeholder={
        instrumentKey && assetClass
          ? 'Search instruments'
          : 'Pick an instrument type and asset class first'
      }
      disabled={!instrumentKey || !assetClass}
      notFound="No matching instruments"
    />
  );
};

export const CollectionSelect = ({value, onChange, skip}) => {
  const pagination = usePaginatedOptions({
    query: GET_ALL_COLLECTIONS,
    skip,
    buildVariables: (page, search, limit) => ({
      filters: {
        paginate: true,
        page,
        limit,
        ...(search && {search}),
      },
    }),
    readPage: data => {
      const result = data?.adminGetAllCollections?.data;
      const page = result?.pagination;
      return {
        rows: (result?.collections ?? []).filter(row => !row?.isDeleted),
        // CollectionFilters pages but the response reports totals rather than
        // a next-page flag, so derive it.
        hasNextPage: page ? page.page * page.limit < page.total : false,
      };
    },
    toOption: row => ({value: row.id, label: row.name, icon: row.iconUrl}),
  });

  return (
    <PaginatedSelect
      value={value}
      onChange={onChange}
      pagination={pagination}
      placeholder="Search collections"
      notFound="No matching collections"
      renderOption={option => (
        <div className="all-data">
          {option.icon && <img src={option.icon} alt="" />}
          <span>{option.label}</span>
        </div>
      )}
    />
  );
};

/** Brokers. One record per venue now, so the label carries the venue. */
export const BrokerSelect = ({value, onChange, mode, skip}) => {
  const {data, loading} = useQuery(GET_ALL_BROKERS, {skip, errorPolicy: 'all'});
  return (
    <Select
      mode={mode}
      value={value ?? undefined}
      onChange={onChange}
      loading={loading}
      // The field that is fetching is the field that is unusable — it has no
      // options yet. Everything else on the panel, Apply included, stays live.
      disabled={loading}
      allowClear
      showSearch
      optionFilterProp="children"
      placeholder={mode === 'multiple' ? 'Any of these brokers' : 'Any broker'}>
      {(data?.getBrokers?.data ?? []).map(broker => (
        <Select.Option key={broker?.id} value={broker?.id}>
          {brokerOptionLabel(broker)}
        </Select.Option>
      ))}
    </Select>
  );
};

export const ExchangeSelect = ({value, onChange, skip}) => {
  const {data, loading} = useQuery(GET_ALL_STOCKS, {skip, errorPolicy: 'all'});
  return (
    <Select
      value={value ?? undefined}
      onChange={onChange}
      loading={loading}
      // The field that is fetching is the field that is unusable — it has no
      // options yet. Everything else on the panel, Apply included, stays live.
      disabled={loading}
      allowClear
      showSearch
      optionFilterProp="children"
      placeholder="Any exchange">
      {(data?.getAllStocksExchange ?? [])
        .filter(item => item?.id)
        .map(item => (
          <Select.Option value={item.id} key={item.id}>
            {item.stockExchangeName}
          </Select.Option>
        ))}
    </Select>
  );
};

export const OrganisationSelect = ({value, onChange, skip}) => {
  const {data, loading} = useQuery(GET_SAVING_COMPANIES, {
    skip,
    errorPolicy: 'all',
  });
  return (
    <Select
      value={value ?? undefined}
      onChange={onChange}
      loading={loading}
      // The field that is fetching is the field that is unusable — it has no
      // options yet. Everything else on the panel, Apply included, stays live.
      disabled={loading}
      allowClear
      showSearch
      optionFilterProp="children"
      placeholder="Any organisation">
      {(data?.getOrganizations?.data ?? []).map(org => (
        <Select.Option key={org?.id} value={org?.id}>
          {org?.name}
        </Select.Option>
      ))}
    </Select>
  );
};
